import axios from "axios";
import { API_URL } from "src/scenes/App";
import { UserKeys } from "src/constants/userDetails";

const authHeaders = () => ({
    headers: {
        Authorization: "Bearer " + localStorage.getItem(UserKeys.TOKEN)
    }
});

const jsonAuthHeaders = () => ({
    headers: {
        Authorization: "Bearer " + localStorage.getItem(UserKeys.TOKEN),
        "Content-Type": "application/json"
    }
});

export const STORAGE_FILE_CATEGORY = {
    IMAGE: "image",
    VIDEO: "video",
    AUDIO: "audio",
    DOCUMENT: "file"
};

export const STORAGE_FILE_STATUS = {
    PENDING_UPLOAD: "pending_upload",
    UPLOADED: "uploaded",
    TRANSCODING: "transcoding",
    READY: "ready",
    FAILED: "failed"
};

export const STORAGE_CATEGORIES = [
    { key: "images", label: "Images" },
    { key: "video", label: "Video" },
    { key: "audio", label: "Audio" },
    { key: "documents", label: "Documents" }
];

const toNumber = value => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const bytesToGb = bytes => bytes / (1024 * 1024 * 1024);
const bytesToMb = bytes => bytes / (1024 * 1024);

const formatGb = value => {
    const number = toNumber(value);
    if (number >= 100 || Number.isInteger(number)) return String(Math.round(number));
    return number.toFixed(1);
};

export const formatStorageSize = bytes => {
    const value = toNumber(bytes);
    if (value <= 0) return "0 MB";

    const gb = bytesToGb(value);
    if (gb >= 0.1) return `${formatGb(gb)} GB`;

    const mb = bytesToMb(value);
    if (mb >= 1) return `${Math.round(mb)} MB`;

    return `${Math.max(Math.round(value / 1024), 1)} KB`;
};

export const formatStorageFileSize = bytes => {
    const value = toNumber(bytes);
    if (value <= 0) return "0 MB";

    const mb = bytesToMb(value);
    if (mb < 1) return `${Math.max(mb, 0.1).toFixed(1)} MB`;
    if (mb < 10) return `${mb.toFixed(1)} MB`;

    return `${Math.round(mb).toLocaleString()} MB`;
};

export const formatStoragePercentage = percentage => {
    const value = toNumber(percentage);

    if (value <= 0) return "0";

    if (value < 0.01) {
        return value.toFixed(4).replace(/\.?0+$/, "");
    }

    if (value < 1) {
        return value.toFixed(2).replace(/\.?0+$/, "");
    }

    return String(Math.round(value));
};

export const inferStorageFileCategory = file => {
    const type = String(file?.fileType || file?.type || file?.mimeType || "").toLowerCase();
    const name = String(file?.fileName || file?.name || file?.filename || "").toLowerCase();

    if (type.includes("image") || /\.(jpg|jpeg|png|gif|webp|svg)$/.test(name)) return STORAGE_FILE_CATEGORY.IMAGE;
    if (type.includes("video") || /\.(mp4|mov|webm|avi|mkv)$/.test(name)) return STORAGE_FILE_CATEGORY.VIDEO;
    if (type.includes("audio") || /\.(mp3|wav|ogg|m4a)$/.test(name)) return STORAGE_FILE_CATEGORY.AUDIO;
    return STORAGE_FILE_CATEGORY.DOCUMENT;
};

const normaliseCategory = (category, totalBytes) => {
    const usedBytes = toNumber(category.usedBytes ?? category.bytes ?? category.sizeBytes);
    const percentage = category.percentage ?? category.percent ?? (totalBytes > 0 ? (usedBytes / totalBytes) * 100 : 0);

    return {
        key: category.key || category.type || category.name,
        label: category.label || category.name || category.type,
        count: toNumber(category.count ?? category.total ?? category.files),
        usedBytes,
        percentage: toNumber(percentage)
    };
};

export const normaliseStorageSummary = data => {
    const totalBytes = toNumber(data.totalBytes ?? data.limitBytes ?? data.storageLimitBytes);
    const rawCategories = data.categories || data.fileTypes || {};
    const categoryList = Array.isArray(rawCategories)
        ? rawCategories
        : STORAGE_CATEGORIES.map(({ key, label }) => ({ key, label, ...(rawCategories[key] || {}) }));
    const categoryUsedBytes = categoryList.reduce((total, category) => total + toNumber(category.usedBytes ?? category.bytes ?? category.sizeBytes), 0);
    const usedBytes = toNumber(data.usedBytes ?? data.storageUsedBytes) || categoryUsedBytes;
    const limitGb = data.limitGb ?? data.totalGb ?? (totalBytes ? bytesToGb(totalBytes) : 0);
    const usedGb = data.usedGb ?? (usedBytes ? bytesToGb(usedBytes) : 0);
    const freeGb = data.freeGb ?? Math.max(toNumber(limitGb) - toNumber(usedGb), 0);
    const calculatedUsagePercentage = toNumber(limitGb) > 0 ? (toNumber(usedGb) / toNumber(limitGb)) * 100 : 0;
    const usagePercentage = toNumber(data.usagePercentage ?? data.percentUsed);
    const displayUsagePercentage = usagePercentage > 0 ? usagePercentage : calculatedUsagePercentage;

    return {
        usedBytes,
        limitBytes: totalBytes || toNumber(limitGb) * 1024 * 1024 * 1024,
        freeBytes: Math.max((totalBytes || toNumber(limitGb) * 1024 * 1024 * 1024) - usedBytes, 0),
        usedGb: toNumber(usedGb),
        freeGb: toNumber(freeGb),
        limitGb: toNumber(limitGb),
        usagePercentage: Math.min(toNumber(displayUsagePercentage), 100),
        categories: categoryList.map(category => normaliseCategory(category, usedBytes)),
        currentStorage: data.currentStorage
    };
};

export const normaliseStorageFile = file => ({
    id: file.id || file.uuid || file.key || file.fileKey,
    name: file.name || file.fileName || file.filename || "filename",
    type: file.category || inferStorageFileCategory(file),
    sizeBytes: toNumber(file.sizeBytes ?? file.bytes ?? file.fileSize),
    previewUrl: file.previewUrl || file.thumbnailUrl || file.url || file.fileUrl || "",
    downloadUrl: file.downloadUrl || file.url || file.fileUrl || "",
    source: file.source || file.attachedTo || file.module || "Site Files",
    status: file.status || STORAGE_FILE_STATUS.READY,
    canDelete: file.canDelete !== false,
    attachedTo: file.attachedTo || file.owner || null,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt
});

const getStorageFileCreatedTime = file => {
    const createdAt = Date.parse(file.createdAt || "");
    return Number.isNaN(createdAt) ? 0 : createdAt;
};

const normaliseStorageAddOn = option => ({
    id: option.id || option.uuid || option.addonUuid,
    addonUuid: option.addonUuid || option.uuid || option.id,
    amountLabel: option.amountLabel || (toNumber(option.purchasedQuantity ?? option.capacityGb) >= 1024 ? "1 TB" : `${option.purchasedQuantity || option.capacityGb} GB`),
    purchasedQuantity: toNumber(option.purchasedQuantity ?? option.capacityGb),
    priceLabel: option.priceLabel || option.price || "",
    description: option.description || "",
});

export const getStorageSummary = async () => {
    const response = await axios.get(`${API_URL}/company/storage/summary`, authHeaders());
    return normaliseStorageSummary(response.data);
};

export const getStorageFiles = async (params) => {
    const response = await axios.get(`${API_URL}/company/storage/files`, {
        ...authHeaders(),
        params: params || {}
    });
    const files = Array.isArray(response.data) ? response.data : response.data.files || [];
    return files
        .map(normaliseStorageFile)
        .sort((firstFile, secondFile) => getStorageFileCreatedTime(secondFile) - getStorageFileCreatedTime(firstFile));
};

export const getStorageFilePreview = async (fileId) => {
    const response = await axios.get(`${API_URL}/company/storage/files/${fileId}/preview`, authHeaders());
    return response.data;
};

export const getStorageFileDownload = async (fileId) => {
    const response = await axios.get(`${API_URL}/company/storage/files/${fileId}/download`, authHeaders());
    return response.data;
};

export const getStorageAddOns = async () => {
    const response = await axios.get(`${API_URL}/company/storage/add-ons`, authHeaders());
    const options = Array.isArray(response.data) ? response.data : response.data.addOns || [];
    return options.map(normaliseStorageAddOn);
};

export const purchaseStorageUpgrade = async (option) => {
    const response = await axios.post(`${API_URL}/company/storage/add-ons/purchase`, {
        addonUuid: option.addonUuid || option.uuid || null
    }, jsonAuthHeaders());
    return response.data;
};

export const deleteStorageFile = async (fileId, options = {}) => {
    const response = await axios.delete(`${API_URL}/company/storage/files/${fileId}`, {
        ...authHeaders(),
        data: options
    });
    return response.data;
};

export const formatStorageGb = formatGb;
